/**
 * description:
 * "Our Fams" button picker. A button per fam on the left, and the selected
 * fam's picture carousel on the right.
 */
import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import FamPictureCarousel from "./FamPictureCarousel";
import { mustard } from "../../../styles.tsx";

/**
 * Picks every image in src/pictures/AteKuya/<folder>/ automatically.
 * Add or remove photos there and they show up.
 */
const famImagesContext = require.context("../../../pictures/AteKuya", true, /\.(png|jpe?g|webp)$/i);

/**
 * Photos normally fill the frame edge to edge, which trims anything taller
 * than the frame. Sometimes that cuts people out entirely
 * so pictures that are listed here are shown whole instead. 
 * Key is "<folder>/<filename>" exactly as on disk.
 */
const showWholePhoto = new Set([
  "exec/02 - Exec2.jpg",
  "exec/04 - Exec4.jpeg",
  "exec/05 - Exec5.jpg",
  "exec/07 - Exec7.jpg",
  "salido/08 - Salido 8.jpg",
  "wood/08 - Wood8.jpg",
]);

const imagesForFam = (folder) =>
  famImagesContext
    .keys()
    .filter((key) => key.startsWith(`./${folder}/`))
    .sort()
    .map((key) => ({
      src: famImagesContext(key),
      fit: showWholePhoto.has(key.replace("./", "")) ? "contain" : "cover",
    }));

/* Family trees on myangkan.com */
const fams = [
  { id: 1, name: "Bean", folder: "bean", tree: "https://myangkan.com/tree/a1a758ba-943e-4d36-9169-f426031e8d8c" },
  { id: 2, name: "Egg", folder: "egg", tree: "https://myangkan.com/tree/8d264f2d-8ab0-4da3-a5b5-d377f7cc6480" },
  { id: 3, name: "Exec", folder: "exec", tree: "https://myangkan.com/tree/4dab3047-250b-4418-9fd7-9f9711501873" },
  { id: 4, name: "Salido", folder: "salido", tree: "https://myangkan.com/tree/295b6cf1-0974-4e27-8381-ec449ae1d784" },
  { id: 5, name: "Wood", folder: "wood", tree: "https://myangkan.com/tree/b425aaf3-b84b-4e24-bec1-a487d47f0369" },
].map((fam) => ({ ...fam, images: imagesForFam(fam.folder) }));

const FamShowcase = () => {
  const [selectedFamId, setSelectedFamId] = useState(fams[0].id);
  const selectedFam = fams.find((fam) => fam.id === selectedFamId);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: "24px", md: "64px" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, flexWrap: "wrap", gap: "8px" }}>
        {fams.map((fam) => (
          <Button
            key={fam.id}
            onClick={() => setSelectedFamId(fam.id)}
            variant={fam.id === selectedFamId ? "contained" : "outlined"}
            color="secondary"
            disableRipple
            disableElevation
            sx={{ justifyContent: "flex-start", py: "10px", px: "20px", "&:active": { transform: "none" } }}
          >
            <Typography variant="h6" sx={{ fontSize: "1.05rem", lineHeight: 1, color: fam.id === selectedFamId ? "background.default" : "secondary.main" }}>
              {fam.name}
            </Typography>
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          width: { xs: "100%", md: "1px" },
          height: { xs: "1px", md: "auto" },
          bgcolor: "secondary.main",
          opacity: 0.3,
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          flex: 1,
          bgcolor: "background.paper",
          borderRadius: "4px",
          p: { xs: "20px", md: "28px" },
          boxShadow: `8px 8px 0 ${mustard}`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
          <Typography variant="h6" sx={{ color: "primary.main", textAlign: "center" }}>
            Meet {selectedFam.name}
          </Typography>
          <FamPictureCarousel key={selectedFam.id} images={selectedFam.images} />

          {selectedFam.tree && (
            <Button
              component="a"
              href={selectedFam.tree}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="secondary"
              disableRipple
              disableElevation
              sx={{
                textTransform: "none",
                py: "10px",
                px: "24px",
                "&:active": { transform: "none" },
              }}
            >
              <Typography variant="body1" sx={{ fontSize: "1rem", lineHeight: 1, color: "background.default" }}>
                See {selectedFam.name}'s family tree →
              </Typography>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FamShowcase;
